import type { Middleware } from 'openapi-fetch'

export interface RefreshedToken {
  access_token: string
  token_type: string
}

/**
 * Creates a middleware that automatically retries a request when the server
 * responds with 401 Unauthorized.
 *
 * `refreshToken` should attempt to obtain a new access token and return it,
 * or return `null` if refresh failed (e.g. session expired).
 *
 * The middleware deduplicates concurrent refresh attempts so only one refresh
 * call is in-flight at a time.
 */
export function createTokenRefreshMiddleware(refreshToken: () => Promise<RefreshedToken | null>): Middleware {
  let refreshPromise: Promise<RefreshedToken | null> | null = null

  return {
    onResponse: async ({ response, request, options }) => {
      if (response.status !== 401) {
        return response
      }

      // Deduplicate concurrent 401 refresh attempts
      if (!refreshPromise) {
        refreshPromise = refreshToken().finally(() => {
          refreshPromise = null
        })
      }

      const data = await refreshPromise

      if (data) {
        return options.fetch(
          new Request(request, {
            headers: new Headers({
              ...Object.fromEntries(request.headers.entries()),
              Authorization: `${data.token_type} ${data.access_token}`,
            }),
          }),
        )
      }

      return response
    },
  }
}
